import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { combineLatest, Observable } from 'rxjs';
import { LoadDataFacade, SetInitialStateFacade } from '../../../../../common/models';
import {
  WorkingHoursInitialState,
  WorkingHoursListLoad,
  WorkingHoursItemLoad,
  WorkingHoursItemDelete,
  WorkingHoursItemUpdate,
  WorkingHoursItemCreate,
  WorkingHoursItemInitialState,
} from './working-hours.actions';
import { WorkingHoursState } from './working-hours.state';
import { take } from 'rxjs/operators';
import { WorkingHoursBrief, WorkingHoursModel } from './models';
import { MatDialogRef } from '@angular/material/dialog';
import { CompartmentItemFacade } from '../../compartment/item/compartment-item.facade';
import { HubItemFacade } from '../item/hub-item.facade';
import { DockItemFacade } from '../../dock/item/dock-item.facade';
import { ConfirmationDialogAfterClosedModel } from '../../../../../ui-kit';

@Injectable({ providedIn: 'root' })
export class WorkingHoursFacade implements LoadDataFacade, SetInitialStateFacade {
  list$: Observable<WorkingHoursBrief[]> = this.store.select(WorkingHoursState.getCalendarList);
  usedDayList$: Observable<number[]> = this.store.select(WorkingHoursState.getUsedDaysList);
  selectedWorkingHours$: Observable<WorkingHoursModel> = this.store.select(WorkingHoursState.getSelectedWorkingHours);
  timezoneId$: Observable<string> = this.store.select(WorkingHoursState.getCompartmentTimezone);
  loaded$: Observable<boolean> = this.store.select(WorkingHoursState.getLoaded);

  workingHoursRelationIds$: Observable<[number, number, number]> = combineLatest([
    this.hubItemFacade.hubId$,
    this.compartmentItemFacade.compartmentId$,
    this.dockItemFacade.dockId$,
  ]).pipe(take(1));

  constructor(
    private store: Store,
    private hubItemFacade: HubItemFacade,
    private dockItemFacade: DockItemFacade,
    private compartmentItemFacade: CompartmentItemFacade,
  ) {}

  loadAll(): void {
    this.workingHoursRelationIds$
      .pipe(take(1))
      .subscribe(([_hubId, compartmentId, dockId]: [number, number, number]) => {
        this.store.dispatch(new WorkingHoursListLoad({ compartmentId, dockId }));
      });
  }

  loadWorkingHoursById(workingHoursId: number): void {
    this.workingHoursRelationIds$.subscribe(([_hubId, compartmentId, dockId]: [number, number, number]) => {
      this.store.dispatch(
        new WorkingHoursItemLoad({
          workingHoursId,
          compartmentId,
          dockId,
        }),
      );
    });
  }

  createWorkingHours(data: WorkingHoursModel): void {
    this.workingHoursRelationIds$.subscribe(([hubId, compartmentId, dockId]: [number, number, number]) =>
      this.store.dispatch(
        new WorkingHoursItemCreate({
          hubId,
          compartmentId,
          dockId,
          data,
        }),
      ),
    );
  }

  updateWorkingHours(data: WorkingHoursModel): void {
    this.workingHoursRelationIds$.subscribe(([_hubId, compartmentId, dockId]: [number, number, number]) => {
      this.store.dispatch(
        new WorkingHoursItemUpdate({
          data,
          compartmentId,
          dockId,
        }),
      );
    });
  }

  deleteWorkingHours(workingHoursIds: number[], dialog: MatDialogRef<ConfirmationDialogAfterClosedModel>): void {
    this.workingHoursRelationIds$.subscribe(([hubId, compartmentId, dockId]: [number, number, number]) =>
      this.store.dispatch(
        new WorkingHoursItemDelete({
          hubId,
          compartmentId,
          dockId,
          dialog,
          workingHoursIds,
        }),
      ),
    );
  }

  itemSetInitialState(): void {
    this.store.dispatch(new WorkingHoursItemInitialState());
  }

  setInitialState(): void {
    this.store.dispatch(new WorkingHoursInitialState());
  }
}
